import { sha256 } from 'crypto-hash';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import { AlertBanner } from '@/components/AlertBanner';
import { Logo } from '@/components/Logo';
import { Megamenu, MobileMenu } from '@/components/Megamenu';
import type {
  ContentfulAlertBannerFragment,
  ContentfulMainHeaderFragment,
  ContentfulSearchOverlayFragment,
} from '@/graphql/contentful/api';
import { useCheckout } from '@/hooks/useCheckout';
import { usePaypalExpressRedirectRecovery } from '@/hooks/usePaypalExpressRedirectRecovery';
import { usePaypalExpressSuccessRedirect } from '@/hooks/usePaypalExpressSuccessRedirect';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { preloadExpressCheckoutChunks } from '@/lib/preloadExpressCheckoutChunks';
import { StorageHelper } from '@/lib/storage';
import { useAlertBanner } from '@/providers/AlertBannerProvider/AlertBannerContext';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { countrySelectorState } from '@/states/countrySelectorState';
import { useSearchState } from '@/states/searchState';
import { theme } from '@/theme/getTheme';

import { NavigationMenu } from './components';
import { HeaderInfo } from './components/HeaderInfo';
import { CompoundProvider } from './context';
import { loadSearchOverlay, loadShoppingBagDrawer } from './lazyOverlays';

import * as S from './Header.styles';

const ShoppingBagDrawer = dynamic(
  () => loadShoppingBagDrawer().then((mod) => mod.ShoppingBagDrawer),
  { ssr: false },
);
const SearchOverlay = dynamic(
  () => loadSearchOverlay().then((mod) => mod.SearchOverlay),
  { ssr: false },
);

export type MainHeaderProps = Partial<ContentfulMainHeaderFragment> & {
  /**
   * BEC-24926: product detail pages hide the alert banner on mobile so the
   * purchase CTA sits above the fold. Desktop keeps it. Opt-in per page rather
   * than matched on the route, so moving a page cannot silently change this.
   */
  alertBannerHiddenOnMobile?: boolean;
  cmsSearchOverlayData?: ContentfulSearchOverlayFragment | null;
  draftMode: boolean;
  isStick?: boolean;
  logoOnly?: boolean;
};

export const Header = (mainHeader: MainHeaderProps) => {
  const {
    snapshot: { isAddPending, notification },
  } = useCheckout();
  usePaypalExpressRedirectRecovery();
  usePaypalExpressSuccessRedirect();
  const [isShrunkState, setIsShrunk] = useState<boolean>(false);
  const { overlayStatus } = useSearchState();
  const isShrunk = overlayStatus === 'OPEN' || isShrunkState;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isScrollUp, setIsScrollUp] = useState<boolean>(true);
  const isScrollUpRef = useRef(true);
  const [isMobileHeader, setIsMobileHeader] = useState<boolean>(false);
  const [alertsHashValue, setAlertsHashValue] = useState<string>('');
  const [shoppingBagDrawerOpen, setShoppingBagDrawerOpen] =
    useState<boolean>(false);
  // Once opened, the overlays stay mounted: SearchOverlay reloads the wishlist page on close.
  const [hasOpenedBag, setHasOpenedBag] = useState<boolean>(false);
  const [hasOpenedSearch, setHasOpenedSearch] = useState<boolean>(false);
  const lastScrollTop = useRef<number>(0);
  const [showMegamenu, setShowMegamenu] = useState<boolean>(false);
  const scrollPosition = useScrollPosition();
  const { country } = useRegionProvider();
  const resize = useWindowWidth();
  const { setHeaderRef } = useConfigProvider();
  const wrapperRef = useRef<HTMLElement>(null);
  const { cookieAlertBanner, setCookieAlertBanner } = useAlertBanner();
  const isCountrySelectionOpen = countrySelectorState.overlayStatus === 'OPEN';

  const storageHelper = new StorageHelper();

  const headerBoxRef = useRef<HTMLDivElement>(null);
  const megamenuRef = useRef<HTMLDivElement>(null);
  const prevMenuOpenRef = useRef<boolean>(false);
  const prevDrawerOpenRef = useRef<boolean>(false);

  const wrapperHeight = wrapperRef.current?.clientHeight ?? 0;
  const hideDesktopNav = wrapperRef.current
    ? scrollPosition > wrapperHeight
    : false;

  const headerBackground =
    mainHeader.background === theme.components.header.defaultBackground
      ? 'default'
      : 'white';

  const handleMenuState = (isOpen: boolean) => {
    setIsMobileMenuOpen(isOpen);
  };

  /*****
    - To optimize memory size, the hash value of the alert message is stored in cookie.
    - When user reads the warning message and clicks the close icon, the hash value of the warning message is stored in the cookie.
    - When the page is refreshed or re-rendered, contentful's alert message hash value is compared to the cookie's hash value.
    - If both hash values are the same, the alert message is already read and closed by user. so the message is not displayed.
    - If the two hash values are not the same, the alert message from contentful is new thing. so the message is displayed.
  */
  useEffect(() => {
    (async () => {
      const hashValue = await sha256(
        JSON.stringify(mainHeader?.alertBannerCollection?.items ?? []),
      );
      setAlertsHashValue(hashValue);
      setCookieAlertBanner(
        storageHelper.getCookie('close_alert_banner') === hashValue,
      );
    })();
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if ((resize ?? window.innerWidth) <= 992) setIsMobileHeader(true);
    else setIsMobileHeader(false);
  }, [resize]);

  useEffect(() => {
    if (!isMobileHeader && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      handleMenuState(false);
    }
  }, [isMobileHeader]);

  const scrollStateHandler = (stateValue: boolean) => {
    if (
      !stateValue &&
      (document.body.scrollTop === 0 ||
        document.documentElement.scrollTop === 0)
    ) {
      return true;
    }

    if (
      stateValue &&
      document.body.scrollTop === 0 &&
      document.documentElement.scrollTop === 0
    ) {
      return false;
    }
    return stateValue;
  };

  const updateIsScrollUpState = (state: boolean) => {
    isScrollUpRef.current = state;
    setIsScrollUp(state);
    setShowMegamenu(state);
  };

  useEffect(() => {
    const offset = 50; //needed to not remove the submenu and show the main content margin.
    const scrollHandler = () => {
      setIsShrunk((prev) => scrollStateHandler(prev));
      const scrollTop =
        document.body.scrollTop || document.documentElement.scrollTop;
      if (Math.abs(scrollTop - lastScrollTop.current) > offset) {
        if (scrollTop > lastScrollTop.current) {
          if (isScrollUpRef.current) {
            updateIsScrollUpState(false);
          }
        } else if (!isScrollUpRef.current || scrollTop === 0) {
          updateIsScrollUpState(true);
        }
      }

      lastScrollTop.current = scrollTop;
    };

    let ticking = false;
    const optimizedScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          scrollHandler();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', optimizedScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', optimizedScroll);
    };
  }, [isCountrySelectionOpen]);

  useEffect(() => {
    if (storageHelper.getCookie('alert_banner_country') !== country.name) {
      storageHelper.removeCookie('close_alert_banner');
      storageHelper.removeCookie('alert_banner_country');
    }
    setCookieAlertBanner(
      storageHelper.getCookie('close_alert_banner') === alertsHashValue,
    );
  }, [country.name]);

  useEffect(() => {
    if (overlayStatus === 'CLOSE') {
      setIsShrunk(false);
    } else {
      setHasOpenedSearch(true);
    }
  }, [overlayStatus]);

  useEffect(() => {
    if (shoppingBagDrawerOpen) {
      setHasOpenedBag(true);
    }
  }, [shoppingBagDrawerOpen]);

  const handleMouseLeave = () => {
    if (overlayStatus !== 'OPEN') {
      setIsShrunk(hideDesktopNav || isMobileMenuOpen);
    }
  };

  const handleMouseOver = () => {
    if (mainHeader.background === 'transparent') setIsShrunk(true);
    updateIsScrollUpState(true);
  };

  useEffect(() => {
    if (wrapperRef.current) {
      setHeaderRef(wrapperRef);
    }
  }, [cookieAlertBanner, isMobileHeader, isShrunk]);

  const closeAlertBanner = () => {
    storageHelper.setCookie('close_alert_banner', alertsHashValue, {
      expires: 400, // chrome,firefox 400days, safari 7days
    });
    storageHelper.setCookie('alert_banner_country', country.name, {
      expires: 400, // chrome,firefox 400days, safari 7days
    });

    setCookieAlertBanner(true);
  };

  const getBackgroundColor = () => {
    if (overlayStatus === 'OPEN' && mainHeader.background === 'transparent') {
      return 'white';
    }
    return mainHeader.background ?? 'white';
  };

  const getTextColor = () => {
    if (overlayStatus === 'OPEN' && mainHeader.background === 'transparent') {
      return 'dark';
    }
    return mainHeader.textColorOption ?? 'dark';
  };

  useEffect(() => {
    if (isAddPending || notification) {
      setShoppingBagDrawerOpen(true);
    }
  }, [isAddPending, notification]);

  useEffect(() => {
    const resolveOverlayExclusivity = (): void => {
      const prevMenuOpen = prevMenuOpenRef.current;
      const prevDrawerOpen = prevDrawerOpenRef.current;

      const justOpenedMenu = !prevMenuOpen && isMobileMenuOpen;
      const justOpenedDrawer = !prevDrawerOpen && shoppingBagDrawerOpen;

      if (justOpenedMenu && shoppingBagDrawerOpen) {
        setShoppingBagDrawerOpen(false);
      } else if (justOpenedDrawer && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }

      prevMenuOpenRef.current = isMobileMenuOpen;
      prevDrawerOpenRef.current = shoppingBagDrawerOpen;
    };

    resolveOverlayExclusivity();
  }, [shoppingBagDrawerOpen, isMobileMenuOpen]);

  return (
    <CompoundProvider value={{ hideDesktopNav }}>
      <S.Wrapper
        ref={wrapperRef}
        $headerBackground={headerBackground}
        $isShrunk={isShrunk}
        $textColorOption={getTextColor()}
        background={getBackgroundColor()}
        data-test-id='HeaderWrapper'
        data-testid='HeaderWrapper'
        onMouseLeave={handleMouseLeave}
        onMouseOver={handleMouseOver}
      >
        <AlertBanner
          alertBanner={
            (mainHeader?.alertBannerCollection
              ?.items as ContentfulAlertBannerFragment[]) ?? []
          }
          closeAlertBanner={closeAlertBanner}
          data-test-id='HeaderAlertBanner'
          hasCookieAlertBanner={!!alertsHashValue && cookieAlertBanner}
          hideOnMobile={mainHeader.alertBannerHiddenOnMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          shoppingBagDrawerOpen={shoppingBagDrawerOpen}
        />
        <S.HeaderBox data-test-id='HeaderBox'>
          <S.InnerWrapper ref={headerBoxRef} data-test-id='HeaderInnerWrapper'>
            <S.ItemBox $logoOnly={mainHeader.logoOnly}>
              <S.LogoBox
                $headerType={mainHeader.type ?? 'Default'}
                $logoOnly={mainHeader.logoOnly ?? false}
                data-test-id='HeaderLogoBox'
              >
                <Logo
                  data-test-id='HeaderLogo'
                  heightDesktop={mainHeader.logoHeightDesktop}
                  heightMobile={mainHeader.logoHeightMobile}
                  isHeader={true}
                  src={mainHeader.logo?.url}
                  widthDesktop={mainHeader.logoWidthDesktop}
                  widthMobile={mainHeader.logoWidthMobile}
                />
              </S.LogoBox>
              {!mainHeader.logoOnly && (
                <S.HeaderIconBox>
                  <Header.HeaderInfo
                    data-test-id='HeaderInfo'
                    headerBackground={headerBackground}
                    isMobileMenuOpen={isMobileMenuOpen}
                    isOpenMobile={isMobileMenuOpen}
                    isShrunk={isShrunk}
                    onMobileMenuHandler={handleMenuState}
                    onShoppingBagDrawerHandler={setShoppingBagDrawerOpen}
                    textColorOption={getTextColor()}
                  />

                  <Header.Menu
                    data-test-id='HeaderMenu'
                    handleCartClick={() => {
                      preloadExpressCheckoutChunks();
                      setShoppingBagDrawerOpen(true);
                    }}
                    headerBackground={headerBackground}
                    isShrunk={isShrunk}
                    textColorOption={getTextColor()}
                  />
                </S.HeaderIconBox>
              )}
            </S.ItemBox>
            <S.MegamenuBox
              ref={megamenuRef}
              $isDisplayed={
                !mainHeader.logoOnly &&
                (!isShrunk || isScrollUp) &&
                (!isCountrySelectionOpen || showMegamenu)
              }
              data-test-id='HeaderMegamenu'
            >
              <Header.Megamenu
                headerBackground={headerBackground}
                isDisplayed={!mainHeader.logoOnly && (!isShrunk || isScrollUp)}
                isShrunk={isShrunk}
                navItems={mainHeader as ContentfulMainHeaderFragment}
                textColorOption={getTextColor()}
              />
            </S.MegamenuBox>
          </S.InnerWrapper>
          {(shoppingBagDrawerOpen || hasOpenedBag) && (
            <ShoppingBagDrawer
              data-test-id='HeaderShoppingBagDrawer'
              isOpen={shoppingBagDrawerOpen}
              onClose={() => setShoppingBagDrawerOpen(false)}
            />
          )}
        </S.HeaderBox>
      </S.Wrapper>
      <MobileMenu
        data-test-id='HeaderMobileMenu'
        headerBackground={headerBackground}
        isMobileMenuOpen={isMobileMenuOpen}
        isOpen={isMobileMenuOpen}
        items={mainHeader as ContentfulMainHeaderFragment}
        onClose={handleMenuState}
        setShoppingBagDrawerOpen={setShoppingBagDrawerOpen}
        shoppingBagDrawerOpen={shoppingBagDrawerOpen}
      />
      {(overlayStatus === 'OPEN' || hasOpenedSearch) && (
        <SearchOverlay
          cmsData={mainHeader.cmsSearchOverlayData}
          data-test-id='HeaderSearchOverlay'
          draftMode={mainHeader.draftMode}
          offset={wrapperHeight}
        />
      )}
    </CompoundProvider>
  );
};

Header.Megamenu = Megamenu;
Header.HeaderInfo = HeaderInfo;
Header.Menu = NavigationMenu;
Header.Content = Header.displayName = 'Header';
